import Radio from "@jbhi-fi/lanyard-ui/components/Radio/Radio";
import TextField from "@jbhi-fi/lanyard-ui/components/TextField/TextField";
import { AutoCompleteType, InputType } from "@jbhi-fi/lanyard-ui/components/TextField/TextField.types";
import { Close, DeleteOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import Grid, { Grid2Props } from "@mui/material/Unstable_Grid2";
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import Papa from "papaparse";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { useCountry } from "src/components/Context/CountryContext";
import PageContext from "src/components/Context/PageContext";
import { Button } from "src/components/Form";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { LocationResponse } from "src/models/locations/LocationResponse";
import { ExceptionTypes, exceptionTypeOptions } from "src/models/staffPriceExceptions/StaffPriceExceptionTypes";
import { ProductSelect } from "src/pages/Messaging/components/ProductSelect";
import { getConsts } from "src/utils/consts";
import { getLocations } from "src/utils/productAppApiHelper";
import { CreateExceptionModel, exceptionSchema } from "src/validationSchemas/createExceptionSchema";
import { LocationSelect } from "./LocationSelect";
import { CalendarIcon, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Moment } from "moment-timezone";

var moment = require("moment-timezone");
moment.tz.setDefault("Australia/Melbourne");

const gridBorder = {
  border: 1,
  borderColor: "grey.400",
} as Grid2Props;

const gridSpacing = {
  paddingY: 4,
  paddingX: 10,
  marginTop: 3,
  marginX: 0,
  marginBottom: 0,
  spacing: 3,
} as Grid2Props;

type ExceptionFormProps = {
  onFormSubmit: (data: CreateExceptionModel) => void;
  onFormError: (data: any) => void;
};

export interface SkuException {
  type: ExceptionTypes;
  isActive: boolean;
  details: string[];
  price?: number;
  authorisedBy: string;
  comment: string;
  startDate?: string;
  endDate: string;
}

export const ExceptionForm: React.FC<ExceptionFormProps> = (props: ExceptionFormProps) => {
  const history = useHistory();
  const formContext = useFormContext();
  const { country, timezone } = useCountry();
  const exceptionTypesWatch = (formContext.watch("type") as ExceptionTypes) || ExceptionTypes.Sku;
  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;
  const [values, setValues] = useState<SkuException[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locations, setLocations] = useState<LocationResponse[]>([]);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [startDateOpen, setStartDateOpen] = useState<boolean>(false);
  const [endDateOpen, setEndDateOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const startDateErrorMsg = "Invalid date and time format";
  const startErrorMsg = "End date cannot be before the start date";
  const genericErrorMsg = "End date is invalid";
  const futureErrorMsg = "End date cannot be before the current date";

  const momentInstance = moment.tz.setDefault(timezone);

  const AutoCompleteTextField = styled(TextField)`
    .MuiInputBase-root {
      border-radius: 0;
      padding-top: 6px;
      padding-bottom: 6px;
    }
  `;

  const columns: GridColDef[] = [
    {
      field: "details",
      headerName: "SKU",
      width: 100,
      valueGetter: (params) => {
        const details = params.row.details;
        return Array.isArray(details) ? details.join(", ") : details;
      },
    },
    {
      field: "price",
      headerName: "Price",
      width: 100,
    },
    {
      field: "authorisedBy",
      headerName: "Authorised By",
      width: 175,
    },
    {
      field: "comment",
      headerName: "Comment",
      width: 250,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 150,
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 150,
    },
    {
      field: "actions",
      headerName: "Action",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const sku = Array.isArray(params.row.details) ? params.row.details[0] : params.row.details;
        return <Button type={"iconOnly"} icon={<DeleteOutlined />} onClick={() => handleRemoveRow(sku)} />;
      },
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer style={{ float: "inline-end", marginTop: "2pt" }}>
        {<GridToolbarExport color={"primary"} size="large" allign="end" />}
      </GridToolbarContainer>
    );
  }

  const handleRemoveRow = (sku: string) => {
    setValues((prevValues) =>
      prevValues.filter(
        (item) =>
          // Compare the first detail value (SKU) to the target SKU
          !(item.details && Array.isArray(item.details) && item.details[0] === sku)
      )
    );
  };

  useEffect(() => {
    fetchLocations();
  }, [country]);

  const fetchLocations = async () => {
    var result = await getLocations(country);
    setLocations(result.results);
  };

  const onSetDate = async () => {
    const output = values.map((x) => {
      x.startDate = startDate?.format("DD/MM/YYYY HH:mm") ?? null;
      x.endDate = endDate?.format("DD/MM/YYYY HH:mm") ?? null;
      return x;
    });
    setValues([...output]);
  };

  const onAddClick = async () => {
    const formValues = formContext.getValues();

    // Check if multiple SKUs are selected
    const skuDetails = Array.isArray(formValues.details) ? formValues.details : [formValues.details];
    try {
      const baseEntry = {
        type: exceptionTypesWatch,
        isActive: true,
        price: formValues.price,
        authorisedBy: userProfile.displayName,
        comment: formValues.comment,
      };

      // Validate one entry to check if the form is valid
      await exceptionSchema.validate(
        {
          ...baseEntry,
          details: [skuDetails[0]],
        },
        { abortEarly: false }
      );

      // If valid, create separate entries for each SKU
      const newEntries = skuDetails.map((sku) => ({
        ...baseEntry,
        details: [sku],
      }));

      // Add all new entries to the values array
      handleAddSkus(newEntries);

      // Reset form fields except for type
      formContext.reset({
        type: exceptionTypesWatch,
        isActive: true,
        details: "",
        price: "",
        authorisedBy: userProfile.displayName,
        comment: "",
      });
      formContext.clearErrors();
    } catch (validationError: any) {
      if (validationError.inner) {
        validationError.inner.forEach((err) => {
          formContext.setError(err.path, {
            type: "manual",
            message: err.message,
          });
        });

        props.onFormError(validationError.inner);
      }
    }
  };

  const onCancelClick = () => {
    const consts = getConsts();
    history.push(consts.routerPath.staffExceptions);
  };

  const onSaveClick = async () => {
    if (exceptionTypesWatch == ExceptionTypes.Sku) {
      const data: CreateExceptionModel = {
        exceptions: values,
      };
      props.onFormSubmit(data);
    } else {
      var entry = await processStoreForm();
      if (entry != null) {
        const data: CreateExceptionModel = {
          exceptions: [entry],
        };
        props.onFormSubmit(data);
      }
    }
  };

  const processStoreForm = async () => {
    const formValues = formContext.getValues();

    // Check if multiple SKUs are selected

    try {
      const entry = {
        type: exceptionTypesWatch,
        isActive: true,
        price: null,
        details: formValues.store,
        authorisedBy: userProfile.displayName,
        comment: formValues.comment,
        startDate: null,
        endDate: null,
      };

      // Validate one entry to check if the form is valid
      await exceptionSchema.validate(entry, { abortEarly: false });

      // Reset form fields except for type
      formContext.reset({
        type: exceptionTypesWatch,
        isActive: true,
        details: "",
        price: "",
        authorisedBy: userProfile.displayName,
        comment: "",
        store: "",
        startDate: null,
        endDate: null,
      });
      formContext.clearErrors();

      return entry;
    } catch (validationError: any) {
      if (validationError.inner) {
        validationError.inner.forEach((err) => {
          formContext.setError(err.path, {
            type: "manual",
            message: err.message,
          });
        });

        props.onFormError(validationError.inner);
      }

      return null;
    }
  };

  const handleRadioChange = (value: string) => {
    formContext.reset({
      type: value,
      isActive: true,
      details: "",
      price: undefined,
      authorisedBy: userProfile.displayName,
      comment: "",
      startDate: null,
      endDate: null,
    });
    setStartDate(null);
    setEndDate(null);
  };

  const handleAddSkus = (newEntries) => {
    if (newEntries && newEntries.length > 0) {
      setValues((prevValues) => [...prevValues, ...newEntries]);
    } else {
      console.error("SKU Exception is not set or invalid.");
    }
  };

  const validateCsvData = (data: any[]): { valid: SkuException[]; errors: string[] } => {
    const validEntries: SkuException[] = [];
    const errors: string[] = [];

    data.forEach((row, index) => {
      const rowNumber = index + 2;
      const rowErrors: string[] = [];

      // Normalize headers
      const normalizedRow: any = {};
      Object.keys(row).forEach((key) => {
        const normalizedKey = key.trim().toLowerCase();
        normalizedRow[normalizedKey] = typeof row[key] === "string" ? row[key].trim() : row[key];
      });

      // Check for required fields
      const sku = normalizedRow["sku"];
      const price = normalizedRow["price"];
      const comment = normalizedRow["comment"];

      if (!sku || sku === "") {
        rowErrors.push(`Row ${rowNumber}: SKU is required`);
      }

      if (!price || price === "") {
        rowErrors.push(`Row ${rowNumber}: Price is required`);
      } else {
        const numericPrice = parseFloat(price);
        if (isNaN(numericPrice)) {
          rowErrors.push(`Row ${rowNumber}: Price must be a number`);
        } else if (numericPrice < 0) {
          rowErrors.push(`Row ${rowNumber}: Price cannot be negative`);
        }
      }

      if (!comment || comment === "") {
        rowErrors.push(`Row ${rowNumber}: Comment is required`);
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      } else {
        // Create valid entry
        validEntries.push({
          type: exceptionTypesWatch,
          isActive: true,
          details: [sku],
          price: parseFloat(price),
          authorisedBy: userProfile.displayName,
          comment: comment,
          startDate: null,
          endDate: null,
        });
      }
    });

    return { valid: validEntries, errors };
  };

  const processCsvFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false, // Keep as strings for validation
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          const parseErrors = results.errors.map((error) => `Parse error at row ${error.row}: ${error.message}`);
          setUploadErrors(parseErrors);
          return;
        }

        if (!results.data || results.data.length === 0) {
          setUploadErrors(["CSV file is empty or contains no valid data"]);
          return;
        }

        // Check for required headers
        const firstRow = results.data[0] as any;
        const headers = Object.keys(firstRow).map((h) => h.trim().toLowerCase());
        const requiredHeaders = ["sku", "price", "comment"];
        const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));

        if (missingHeaders.length > 0) {
          setUploadErrors([`Missing required headers: ${missingHeaders.join(", ")}`]);
          return;
        }

        const { valid, errors } = validateCsvData(results.data as any[]);

        if (errors.length > 0) {
          setUploadErrors(errors);
          return;
        }

        if (valid.length === 0) {
          setUploadErrors(["No valid entries found in CSV"]);
          return;
        }

        // Clear any previous errors and add valid entries
        setUploadErrors([]);
        setValues((prevValues) => [...prevValues, ...valid]);

        console.log(`Successfully uploaded ${valid.length} entries from CSV`);
      },
      error: (error) => {
        setUploadErrors([`Failed to parse CSV: ${error.message}`]);
      },
    });
  };

  const resetValues = () => {
    setValues([]);
    setUploadErrors([]);
  };

  const handleBulkUpload = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch("/staff-price-exceptions-template.csv");

      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "staff-price-exceptions-template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      // Optionally show user-friendly error message
      alert("Failed to download template. Please try again or contact support.");
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setUploadErrors(["Please select a CSV file"]);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      setUploadErrors(["File size must be less than 5MB"]);
      return;
    }

    processCsvFile(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (!hasPermission(UserRoles.PricingStaffExceptions, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }

  const renderBulkUpload = function (isLoading: boolean, isDownloading: boolean) {
    return (
      <Button type="secondary" onClick={handleBulkUpload} disabled={isLoading || isDownloading}>
        Bulk Upload
      </Button>
    );
  };

  const resetStartDate = () => {
    setStartDate(null);
    setErrors([...errors].filter((x) => x !== startDateErrorMsg && x !== startErrorMsg));
  };
  const resetEndDate = () => {
    setEndDate(null);
    setErrors([...errors].filter((x) => x !== genericErrorMsg && x !== startErrorMsg && x !== futureErrorMsg));
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={momentInstance}>
        <form>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv" style={{ display: "none" }} />

          <Grid container {...gridBorder} {...gridSpacing}>
            {uploadErrors.length > 0 && (
              <Grid xs={12}>
                <Box
                  sx={{
                    backgroundColor: "#ffebee",
                    border: "1px solid #f44336",
                    borderRadius: 1,
                    padding: 2,
                    marginBottom: 2,
                  }}
                >
                  <h4 style={{ margin: "0 0 8px 0", color: "#d32f2f" }}>Upload Errors:</h4>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {uploadErrors.map((error, index) => (
                      <li key={index} style={{ color: "#d32f2f" }}>
                        {error}
                      </li>
                    ))}
                  </ul>
                </Box>
              </Grid>
            )}
            {exceptionTypesWatch === ExceptionTypes.Sku && (
              <>
                <Grid xs={12}>
                  <label>SKU</label>
                  <Controller
                    name="details"
                    control={formContext.control}
                    render={({ field }) => {
                      const detailsError = formContext.formState.errors?.details;
                      let errorMessage = "";

                      if (detailsError) {
                        if (typeof detailsError.message === "string") {
                          // Direct message on the array
                          errorMessage = detailsError.message;
                        } else if (Array.isArray(detailsError) && detailsError[0]?.message) {
                          // Message on first array element
                          errorMessage = detailsError[0].message;
                        } else if (detailsError.message) {
                          // Fallback to message property
                          errorMessage = detailsError.message.toString();
                        }
                      }

                      return (
                        <ProductSelect
                          name={field.name}
                          value={field.value ?? []}
                          onChange={field.onChange}
                          hasError={!!detailsError}
                          errorMessage={errorMessage}
                          showBulkUpload={true}
                          bulkUploadComponent={renderBulkUpload}
                          showExport={false}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid xs={6}>
                  <label>SKU Price</label>
                  <Controller
                    name="price"
                    control={formContext.control}
                    render={({ field }) => {
                      const handleChange = (e) => {
                        field.onChange(e);
                        // Clear error when field has value
                        if (e.target.value) {
                          formContext.clearErrors("price");
                        }
                      };

                      return (
                        <TextField
                          id={field.name}
                          name={field.name}
                          value={field.value}
                          onChange={handleChange}
                          type={InputType.number}
                          min="0"
                          autoComplete={AutoCompleteType.off}
                          hasError={!!formContext.formState.errors?.price}
                          errorMsg={formContext.formState.errors.price?.message?.toString() || ""}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid xs={6}>
                  <label>Comment</label>
                  <Controller
                    name="comment"
                    control={formContext.control}
                    render={({ field }) => {
                      const handleChange = (e) => {
                        field.onChange(e);
                        // Clear error when field has value
                        if (e.target.value.trim()) {
                          formContext.clearErrors("comment");
                        }
                      };

                      return (
                        <TextField
                          id={field.name}
                          name={field.name}
                          value={field.value}
                          onChange={handleChange}
                          autoComplete={AutoCompleteType.off}
                          hasError={!!formContext.formState.errors?.comment}
                          errorMsg={formContext.formState.errors.comment?.message?.toString() || ""}
                        />
                      );
                    }}
                  />
                </Grid>

                <Grid xs={12} display="flex" justifyContent="end">
                  <Button type="primary" onClick={onAddClick}>
                    Add
                  </Button>
                </Grid>
              </>
            )}

            {exceptionTypesWatch == ExceptionTypes.Store && (
              <>
                <Grid xs={12}>
                  <label>Store</label>
                  <Controller
                    name="store"
                    control={formContext.control}
                    render={({ field }) => {
                      const detailsError = formContext.formState.errors?.details;
                      let errorMessage = "";

                      if (detailsError) {
                        if (typeof detailsError.message === "string") {
                          // Direct message on the array
                          errorMessage = detailsError.message;
                        } else if (Array.isArray(detailsError) && detailsError[0]?.message) {
                          // Message on first array element
                          errorMessage = detailsError[0].message;
                        } else if (detailsError.message) {
                          // Fallback to message property
                          errorMessage = detailsError.message.toString();
                        }
                      }
                      return (
                        <LocationSelect
                          name={field.name}
                          value={field.value ?? []}
                          onChange={field.onChange}
                          hasError={!!detailsError}
                          errorMessage={errorMessage}
                          locations={locations}
                        />
                      );
                    }}
                  />
                </Grid>
                <Grid xs={12}>
                  <label>Comment</label>
                  <Controller
                    name="comment"
                    control={formContext.control}
                    render={({ field }) => {
                      const handleChange = (e) => {
                        field.onChange(e);
                        // Clear error when field has value
                        if (e.target.value.trim()) {
                          formContext.clearErrors("comment");
                        }
                      };

                      return (
                        <TextField
                          id={field.name}
                          name={field.name}
                          value={field.value}
                          onChange={handleChange}
                          autoComplete={AutoCompleteType.off}
                          hasError={!!formContext.formState.errors?.comment}
                          errorMsg={formContext.formState.errors.comment?.message?.toString() || ""}
                        />
                      );
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {exceptionTypesWatch == ExceptionTypes.Sku && (
            <>
              <Grid container {...gridBorder} {...gridSpacing}>
                <Grid xs={12} display="flex" justifyContent="space-between"></Grid>
                <Grid xs={12}>
                  <h2>Start / End Dates (optional)</h2>
                  <br></br>
                  <p style={{ color: "gray" }}>
                    This will set start and end dates to all exceptions in this list. If no start date is set it will be
                    active immediately. If no end date is set it will run indefinitely
                  </p>
                </Grid>
                <Grid xs={12}></Grid>
                <Grid xs={12} container justifyContent="center" alignItems="center" rowSpacing={3} columnSpacing={2}>
                  <Grid xs={6}>
                    <DateTimePicker
                      open={startDateOpen}
                      onClose={() => setStartDateOpen(false)} // This is called when the picker requests to be closed
                      onAccept={() => setStartDateOpen(false)}
                      sx={{ width: "100%" }}
                      label="Start Time"
                      value={startDate}
                      onChange={(newValue: Moment | null) => {
                        let newErrors = [...errors];
                        if (newValue && newValue.isValid()) {
                          newErrors = newErrors.filter((err) => err !== startDateErrorMsg);
                        } else {
                          if (!errors.includes(startDateErrorMsg)) {
                            newErrors.push(startDateErrorMsg);
                          }
                        }
                        if (newValue && endDate && endDate.isBefore(newValue, "minute")) {
                          if (!newErrors.includes(startErrorMsg)) {
                            newErrors.push(startErrorMsg);
                          }
                        } else {
                          newErrors = newErrors.filter((err) => err !== startErrorMsg);
                        }
                        setErrors(newErrors);
                        if (newErrors.length === 0) {
                          setStartDate(newValue);
                        }
                      }}
                      format="DD/MM/YYYY HH:mm"
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <>
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      setStartDateOpen(true);
                                    }}
                                    edge="end"
                                  >
                                    <CalendarIcon />
                                  </IconButton>
                                </InputAdornment>
                                <InputAdornment position="end">
                                  <IconButton onClick={resetStartDate} edge="end">
                                    <Close />
                                  </IconButton>
                                </InputAdornment>
                              </>
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid xs={6}>
                    <DateTimePicker
                      sx={{ width: "100%" }}
                      label="End Date"
                      value={endDate}
                      onClose={() => setEndDateOpen(false)} // This is called when the picker requests to be closed
                      onAccept={() => setEndDateOpen(false)}
                      open={endDateOpen}
                      onChange={(newValue: Moment | null) => {
                        const minValidDate = moment();
                        let newErrors = [...errors];
                        if (newValue && newValue.isValid()) {
                          newErrors = newErrors.filter((err) => err !== genericErrorMsg);
                        } else {
                          if (!newErrors.includes(genericErrorMsg)) {
                            newErrors.push(genericErrorMsg);
                          }
                        }
                        if (newValue && newValue.isSameOrAfter(minValidDate, "day")) {
                          newErrors = newErrors.filter((err) => err !== futureErrorMsg);
                        } else {
                          if (!newErrors.includes(futureErrorMsg)) {
                            newErrors.push(futureErrorMsg);
                          }
                        }
                        if (newValue && startDate && newValue.isBefore(startDate, "minute")) {
                          if (!newErrors.includes(startErrorMsg)) {
                            newErrors.push(startErrorMsg);
                          }
                        } else {
                          newErrors = newErrors.filter((err) => err !== startErrorMsg);
                        }
                        setErrors(newErrors);
                        if (newErrors.length === 0) {
                          setEndDate(newValue);
                        }
                      }}
                      format="DD/MM/YYYY HH:mm"
                      minDate={moment()}
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <>
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      setEndDateOpen(true);
                                    }}
                                    edge="end"
                                  >
                                    <CalendarIcon />
                                  </IconButton>
                                </InputAdornment>
                                <InputAdornment position="end">
                                  <IconButton onClick={resetEndDate} edge="end">
                                    <Close />
                                  </IconButton>
                                </InputAdornment>
                              </>
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid xs={12} display="flex" justifyContent="end">
                    <Button type="primary" onClick={onSetDate} disabled={values.length === 0 && errors.length === 0}>
                      Set
                    </Button>
                  </Grid>
                </Grid>

                {errors.length > 0 && (
                  <>
                    <Grid xs={12}></Grid>
                    <Grid xs={12}>
                      <Box
                        sx={{
                          backgroundColor: "#ffebee",
                          border: "1px solid #f44336",
                          borderRadius: 1,
                          padding: 2,
                          marginBottom: 2,
                        }}
                      >
                        <h4 style={{ margin: "0 0 8px 0", color: "#d32f2f" }}>Date Errors:</h4>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {errors.map((error, index) => (
                            <li key={index} style={{ color: "#d32f2f" }}>
                              {error}
                            </li>
                          ))}
                        </ul>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid container sx={{ width: "100%", mt: 4 }} {...gridBorder} paddingBottom={5.5}>
                <Grid xs={12} display="flex" justifyContent="space-between">
                  <DataGrid
                    density="compact"
                    components={{ Toolbar: CustomToolbar }}
                    componentsProps={{
                      toolbar: {
                        csvOptions: {
                          fields: ["details", "price", "authorisedBy", "comment"],
                        },
                      },
                    }}
                    rows={values}
                    getRowId={() => crypto.randomUUID()}
                    sx={{ padding: "0px 32px", margin: "none", border: "none", height: "400px" }}
                    columns={columns}
                    pageSize={100}
                    rowsPerPageOptions={[100]}
                    disableSelectionOnClick
                    experimentalFeatures={{ newEditingApi: true }}
                    // hideFooterPagination={true}
                  />
                </Grid>
                <Grid xs={12} display="flex" justifyContent="space-between" paddingLeft={11.5}>
                  <Button type="secondary" onClick={resetValues}>
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
          <Grid container marginTop={3}>
            <Grid xs={12} display="flex" justifyContent="space-between">
              <Button type="link" onClick={onCancelClick}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={onSaveClick}
                disabled={values.length === 0 && exceptionTypesWatch == ExceptionTypes.Sku}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </form>
      </LocalizationProvider>
      <Dialog open={isModalOpen} onClose={handleModalClose}>
        <DialogTitle>Bulk Upload SKUs</DialogTitle>
        <DialogContent>
          <Typography>
            Please upload a <strong>CSV</strong> file containing skus only. <br />
            <Link onClick={downloadTemplate} download style={{ cursor: "pointer" }}>
              Download sample CSV template here
            </Link>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleModalConfirm}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
